
import type { PropType } from 'vue'
import type {
  AuditItem,
  Dictionary,
  FeatureNode,
  LinkedListItem,
  WidgetOptions,
  WidgetOptionsItem,
  WidgetSetup,
} from '../../models'
import type {
  AuditObjectTypeConfig,
  AuditObjectTypeConfigProvider,
} from '../../providers/interfaces/audit-object-type-config.provider'
import type { NoDataMessage } from './no-data.component.vue'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { defineComponent } from 'vue'

import { mapActions, mapGetters } from 'vuex'

import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { FeatureIdEnum, WelcomeModeEnum } from '../../enums'

import { capitalizeAllWords, capitalizeSentence } from '../../filters'

import { APP_CONSTS } from '../../store/app/app.consts'

import { AUDIT_CONSTS } from '../../store/audit/audit.consts'
import { AUTH_CONSTS } from '../../store/auth/auth.consts'
import LinkedList from '../linked-list/linked-list.component.vue'
import Widget from '../widget.component.vue'
import NoData from './no-data.component.vue'

interface WelcomeConfig {
  collapsed: Array<string>
}

interface WelcomeSetup extends WidgetSetup {
  config: WelcomeConfig
}

export default defineComponent({
  name: 'WelcomeWidget',
  components: {
    NoData,
    Widget,
    LinkedList,
  },
  props: {
    period: {
      type: Object as PropType<{ startDate: Date; endDate: Date }>,
      required: true,
    },
    setup: Object as PropType<WelcomeSetup>,
    noData: Object as PropType<Dictionary<string>>,
  },
  data(): Record<string, any> {
    return {
      baseClass: 'epb-welcome-widget',
      messages: this.$epb.messages.highlights.widget.welcome,
      loadingData: true,
    }
  },
  computed: {
    ...mapGetters(AUTH_CONSTS.moduleName, {
      tokenInfo: AUTH_CONSTS.getters.tokenInfo,
    }),
    ...mapGetters(AUDIT_CONSTS.moduleName, {
      audits: AUDIT_CONSTS.getters.data,
    }),
    ...mapGetters(APP_CONSTS.moduleName, {
      featuresTree: APP_CONSTS.getters.featuresTree,
    }),
    auditCanBeVisible(): boolean {
      return !this.$epb.useOldWelcome && this.hasAuditPermission
    },
    options(): WidgetOptions {
      return this.setup?.options || this.defaultOptions
    },
    defaultOptions(): WidgetOptions {
      return {
        items: Object.values(WelcomeModeEnum).map(
          (welcomeMode: WelcomeModeEnum): WidgetOptionsItem => ({
            id: welcomeMode,
            label: capitalizeSentence(welcomeMode),
            selected: welcomeMode === WelcomeModeEnum.EXPANDED,
          }),
        ),
        documentationLink: this.messages.link,
      }
    },
    welcomeMessage(): string {
      const titleMessages = this.messages.titles
      const time = new Date().getHours()
      if (time > 6 && time < 13) {
        return titleMessages.morning
      }
      return titleMessages.afternoon
    },
    userFirstName(): string {
      return capitalizeAllWords(this.tokenInfo.user.firstName || '')
    },
    hasAuditPermission(): boolean {
      return this.featuresTree[DomainEnum.GLOBAL].some(
        (node: FeatureNode) => node.id === FeatureIdEnum.AUDIT,
      )
    },
    linkedListItems(): Array<LinkedListItem> {
      if (!this.audits?.length) {
        return []
      }
      const getAuditObjectTypeConfigProvider = CONTAINER.get<AuditObjectTypeConfigProvider>(
        TYPES.AuditObjectTypeConfigProvider,
      )
      return this.audits.reduce((items: Array<LinkedListItem>, item: AuditItem) => {
        const date = this.getDate(item.date)
        const auditObjectTypeConfig = getAuditObjectTypeConfigProvider(item.payload.objectType)
        const linkedListItem: LinkedListItem = this.buildLinkedListItem(item, auditObjectTypeConfig)
        const linkedListItemParent = items.find(item => item.id === date)
        if (linkedListItemParent) {
          linkedListItemParent.children.push(linkedListItem)
        } else {
          const linkedListItemParent: LinkedListItem = this.buildLinkedListItemParent(
            date,
            linkedListItem,
          )
          items.push(linkedListItemParent)
        }
        return items
      }, [])
    },
    config(): WelcomeConfig {
      return this.setup?.config || this.defaultConfig
    },
    defaultConfig(): WelcomeConfig {
      return {
        collapsed: [],
      }
    },
    selectedOption(): WidgetOptionsItem {
      return this.options.items.find(item => item.selected)
    },
    isCollapsed(): boolean {
      return this.selectedOption?.id === WelcomeModeEnum.COLLAPSED
    },
    noDataMessage(): NoDataMessage {
      return {
        title: this.noData.titleWelcome,
      }
    },
  },
  async mounted(): Promise<void> {
    if (this.auditCanBeVisible) {
      await this.loadData()
    }
  },
  methods: {
    ...mapActions(AUDIT_CONSTS.moduleName, {
      read: AUDIT_CONSTS.actions.read,
    }),
    getImageUrl(image: string): string {
      return require(`../../assets/media/image/highlights/${image}.svg`)
    },
    async loadData(): Promise<void> {
      this.loadingData = true
      this.$emit('updateLoading', { visible: true })
      try {
        await this.read({
          pagination: {
            page: 1,
            itemsPerPage: -1,
          },
          filters: {
            ...this.period,
            endDate: new Date(),
            user: this.tokenInfo.user.name,
          },
        })
      } catch (error) {
        this.$emit('showError', this.$epb.utils.decodeErrorMessage(error))
      } finally {
        this.loadingData = false
        this.$emit('updateLoading', { visible: false })
      }
    },
    buildLinkedListItem(
      item: AuditItem,
      auditObjectTypeConfig: AuditObjectTypeConfig,
    ): LinkedListItem {
      return {
        id: item.id,
        icon: auditObjectTypeConfig.icon,
        title: this.buildLinkedListItemTitle(item, auditObjectTypeConfig),
        subtitle: this.getTime(item.date),
      }
    },
    buildLinkedListItemTitle(
      item: AuditItem,
      auditObjectTypeConfig: AuditObjectTypeConfig,
    ): string {
      const featureName = auditObjectTypeConfig.featureName
      const action = this.messages.audit.actions[item.payload.action.toLocaleLowerCase()]
      const queries = auditObjectTypeConfig.getItemQueries(item.payload)
      const message = this.getAuditMessage(queries.length)

      return this.$epb.utils.compileMessage(message, {
        feature: `<span class="${this.baseClass}__audit-feature">${featureName}</span>`,
        action,
        ...(queries.length && {
          query: `<span class="${this.baseClass}__audit-query">${queries[0]}</span>`,
          count: `<span class="${this.baseClass}__audit-count">${queries.length - 1} more</span>`,
        }),
      })
    },
    getAuditMessage(queryCount: number): string {
      const auditMessages = this.messages.audit
      if (!queryCount) {
        return auditMessages.default
      }
      return queryCount > 1 ? auditMessages.multiple : auditMessages.simple
    },
    buildLinkedListItemParent(date: string, linkedListItem: LinkedListItem): LinkedListItem {
      return {
        id: date,
        title: date,
        expanded: !this.isCollapsedDay(date),
        outlined: true,
        children: [linkedListItem],
      }
    },
    isCollapsedDay(date: string): boolean {
      return this.config.collapsed.includes(date)
    },
    getTime(date: Date): string {
      return date.toLocaleString(this.$epb.locale, {
        hourCycle: 'h23',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    getDate(date: Date): string {
      const options: Intl.DateTimeFormatOptions = {
        weekday: 'short',
        day: 'numeric',
      }
      const parsedDate = date.toLocaleString(this.$epb.locale, options)
      const today = new Date().toLocaleString(this.$epb.locale, options)
      return parsedDate === today ? this.messages.today : parsedDate
    },
    onItemClick(item: LinkedListItem): void {
      const collapsedList: string[] = item.expanded
        ? [...this.config.collapsed, item.id]
        : this.config.collapsed.filter(collapsedDay => collapsedDay !== item.id)

      this.$emit('onSetupChange', {
        config: {
          collapsed: this.removeOldDays(collapsedList),
        },
      })
    },
    removeOldDays(collapsedList: string[]): string[] {
      return collapsedList.reduce((list, day) => {
        if (this.linkedListItems.some((linkedItem: LinkedListItem) => linkedItem.id === day)) {
          list.push(day)
        }
        return list
      }, [])
    },
  },
})
